import React from 'react';
import { Button, Tag } from 'antd';
import {
  TeamOutlined,
  PhoneOutlined,
  MailOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  TagOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import CopyIconButton from '../common/CopyIconButton';
import SendSmsIconButton from '../common/SendSmsIconButton';
import ClickToCallButton from '../call-center/ClickToCallButton';
import CustomerDealSummary from './CustomerDealSummary';
import { getLeadTypeIcon, isOrganizationUserType } from '../../utils/leadTypeUtils';
import { resolveRelationLabel } from '../../utils/sourceUtils';

const ContactField = ({ icon, label, value, actions }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <div className="w-8 h-8 rounded-lg bg-white/80 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 flex items-center justify-center shrink-0 text-gray-400">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-0.5 custom-font">{label}</div>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate custom-font" dir="ltr">
            {value}
          </span>
          {actions}
        </div>
      </div>
    </div>
  );
};

const EmptyContactState = () => (
  <div className="px-4 sm:px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-l from-gray-50/80 to-white dark:from-gray-900/40 dark:to-gray-900/20">
    <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 px-5 py-8 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <InfoCircleOutlined className="text-xl text-gray-400" />
      </div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 m-0 mb-1 custom-font">
        مشتری ندارد
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 m-0 custom-font">
        سرنخ یا مشتری مرتبطی برای این معامله ثبت نشده است.
      </p>
    </div>
  </div>
);

const LeadContactPanel = ({ lead, navigate, onSendSms }) => {
  const LeadTypeIcon = getLeadTypeIcon(lead?.user_type);
  const isOrg = isOrganizationUserType(lead?.user_type);
  const categoryLabel = resolveRelationLabel(lead?.category);
  const sourceLabel = resolveRelationLabel(lead?.source);
  const displayName = lead?.name || lead?.client_name || '—';

  return (
    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-l from-amber-50/70 via-orange-50/30 to-white dark:from-amber-950/20 dark:via-gray-900/30 dark:to-gray-900/20">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-0.5 h-4 bg-amber-500 rounded-full" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 m-0 custom-font">
              اطلاعات سرنخ
            </h3>
            <Tag color="orange" className="m-0 text-[11px] px-2 py-0 custom-font border-amber-200">
              تبدیل به مشتری نشده
            </Tag>
          </div>
          {lead?.id && (
            <Button
              type="default"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/leads/${lead.id}`)}
              className="custom-font shrink-0"
            >
              مشاهده سرنخ
            </Button>
          )}
        </div>

        <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-white/90 dark:bg-gray-900/50 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-amber-50 dark:border-amber-900/20 bg-gradient-to-l from-amber-50/60 to-transparent dark:from-amber-950/10">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                <LeadTypeIcon className="text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-base font-bold text-gray-900 dark:text-white custom-font truncate">
                    {displayName}
                  </span>
                  {isOrg && (
                    <Tag className="m-0 text-[10px] custom-font" color="gold">
                      حقوقی
                    </Tag>
                  )}
                </div>
                {lead?.company_name && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 custom-font">
                    <BankOutlined />
                    <span>{lead.company_name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ContactField
              icon={<PhoneOutlined />}
              label="موبایل"
              value={lead?.mobile}
              actions={
                lead?.mobile ? (
                  <>
                    <CopyIconButton text={lead.mobile} />
                    <ClickToCallButton phone={lead.mobile} />
                    {onSendSms ? <SendSmsIconButton onClick={onSendSms} /> : null}
                  </>
                ) : null
              }
            />
            <ContactField
              icon={<MailOutlined />}
              label="ایمیل"
              value={lead?.email}
              actions={lead?.email ? <CopyIconButton text={lead.email} /> : null}
            />
            {categoryLabel && (
              <ContactField
                icon={<TagOutlined />}
                label="دسته‌بندی"
                value={categoryLabel}
              />
            )}
            {sourceLabel && (
              <ContactField
                icon={<LinkOutlined />}
                label="شیوه آشنایی"
                value={sourceLabel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerContactPanel = ({ customer, navigate, onSendSms, returnTo }) => (
  <div className="border-b border-gray-200 dark:border-gray-800">
    <div className="px-4 sm:px-6 py-4 bg-gradient-to-l from-emerald-50/70 via-teal-50/30 to-white dark:from-emerald-950/20 dark:via-gray-900/30 dark:to-gray-900/20">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-0.5 h-4 bg-emerald-500 rounded-full" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 m-0 custom-font">
              اطلاعات مشتری
            </h3>
            <Tag color="green" className="m-0 text-[11px] px-2 py-0 custom-font">
              مشتری فعال
            </Tag>
          </div>
          {customer?.id && (
            <Button
              type="primary"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() =>
                navigate(`/clients/${customer.id}`, {
                  state: returnTo ? { returnTo } : undefined,
                })
              }
              className="custom-font shrink-0 bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
            >
              مشاهده مشتری
            </Button>
          )}
        </div>

        <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-white/90 dark:bg-gray-900/50 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-emerald-50 dark:border-emerald-900/20 bg-gradient-to-l from-emerald-50/60 to-transparent dark:from-emerald-950/10">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
                <TeamOutlined className="text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-base font-bold text-gray-900 dark:text-white custom-font truncate block">
                  {customer?.name || '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ContactField
              icon={<PhoneOutlined />}
              label="موبایل"
              value={customer?.mobile}
              actions={
                customer?.mobile ? (
                  <>
                    <CopyIconButton text={customer.mobile} />
                    <ClickToCallButton phone={customer.mobile} />
                    {onSendSms ? <SendSmsIconButton onClick={onSendSms} /> : null}
                  </>
                ) : null
              }
            />
            <ContactField
              icon={<MailOutlined />}
              label="ایمیل"
              value={customer?.email}
              actions={customer?.email ? <CopyIconButton text={customer.email} /> : null}
            />
          </div>
        </div>
      </div>
    </div>

    <CustomerDealSummary customer={customer} />
  </div>
);

const DealRelatedContactPanel = ({ customer, lead, navigate, onSendSms, returnTo }) => {
  if (customer?.id) {
    return (
      <CustomerContactPanel
        customer={customer}
        navigate={navigate}
        onSendSms={onSendSms}
        returnTo={returnTo}
      />
    );
  }

  if (lead?.id) {
    return (
      <LeadContactPanel
        lead={lead}
        navigate={navigate}
        onSendSms={onSendSms}
      />
    );
  }

  return <EmptyContactState />;
};

export default DealRelatedContactPanel;
